/**
 * DataGridCellDate — Date picker cell variant
 *
 * Renders a Popover + Calendar that opens immediately on mount.
 * Commits on date selection. Cancels when popover closes without selection.
 */

import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import type { DataGridCellVariantProps } from './types';

function parseDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  const d = new Date(String(value));
  return isNaN(d.getTime()) ? undefined : d;
}

function formatDate(date: Date): string {
  // Return YYYY-MM-DD format for Frappe
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function DataGridCellDate({
  value,
  onCommit,
  onCancel,
  onStartEditing,
  saveState,
}: DataGridCellVariantProps) {
  const [open, setOpen] = useState(false);
  const selectedRef = useRef(false);
  const currentDate = parseDate(value);

  // Open popover on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
      onStartEditing();
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      selectedRef.current = true;
      onCommit(formatDate(date));
      setOpen(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen && !selectedRef.current) {
      onCancel();
    }
  };

  const displayValue = currentDate
    ? currentDate.toLocaleDateString()
    : String(value ?? '');

  return (
    <div
      className="relative flex items-center w-full h-full"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          setOpen(false);
          onCancel();
        } else if (e.key !== 'Tab') {
          e.stopPropagation();
        }
      }}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button className="w-full h-full text-left text-sm px-1 bg-transparent outline-none">
            {displayValue}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
          <Calendar
            mode="single"
            selected={currentDate}
            onSelect={handleSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {saveState.status === 'saving' && (
        <Loader2 className="absolute top-0.5 right-0.5 w-3 h-3 text-muted-foreground animate-spin" />
      )}
    </div>
  );
}
